import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  editUserDetailsSchema,
  type EditUserDetailsFormData,
} from "../schemas/userFormSchema";
import type { UserDto } from "../types";

interface EditUserDetailsFormProps {
  user: UserDto;
  onSubmit: (data: EditUserDetailsFormData) => void | Promise<void>;
  onCancel: () => void;
}

export function EditUserDetailsForm({
  user,
  onSubmit,
  onCancel,
}: EditUserDetailsFormProps) {
  const form = useForm<EditUserDetailsFormData>({
    resolver: zodResolver(editUserDetailsSchema),
    defaultValues: {
      email: user.email,
      firstName: user.firstName,
      middleName: user.middleName || "",
      lastName: user.lastName,
      mobileNumber: user.mobileNumber || "",
      isActive: user.isActive,
    },
  });

  const handleSubmit = async (data: EditUserDetailsFormData) => {
    await onSubmit(data);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-6"
      >
        {/* Account Information Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground">
            Account Information
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-sm font-medium">Username</Label>
              <Input
                value={user.username}
                readOnly
                className="bg-muted"
              />
              <p className="text-[0.8rem] text-muted-foreground">
                Username cannot be changed after creation
              </p>
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Enter email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        {/* Personal Information Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground">
            Personal Information
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="First name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="middleName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Middle Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Middle name (optional)"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Last name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="birthdate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Birthdate</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(e.target.value || null)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="startDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Start Date</FormLabel>
                  <FormControl>
                    <Input
                      type="date"
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) =>
                        field.onChange(e.target.value || null)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <Separator />

        {/* Employment Information Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground">
            Employment Information
          </h4>

          <div className="space-y-1">
            <Label className="text-sm font-medium">Employee ID Number</Label>
            <Input
              value={user.employeeIdNumber || "—"}
              readOnly
              className="bg-muted"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label className="text-sm font-medium">Department</Label>
              <Input
                value={user.departmentName || "—"}
                readOnly
                className="bg-muted"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-sm font-medium">Position</Label>
              <Input
                value={user.positionName || "—"}
                readOnly
                className="bg-muted"
              />
            </div>
          </div>

          <FormField
            control={form.control}
            name="mobileNumber"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mobile Number</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Mobile number (optional)"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        {/* Status & Roles Section */}
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-muted-foreground">
            Status & Roles
          </h4>

          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <FormLabel>Active Status</FormLabel>
                  <FormDescription>
                    User can log in when active
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Read-only Roles Display */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Roles</Label>
            <div className="flex flex-wrap gap-2 rounded-lg border p-3">
              {user.roles.length > 0 ? (
                user.roles.map((role) => (
                  <Badge key={role} variant="secondary">
                    {role}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-muted-foreground">
                  No roles assigned
                </span>
              )}
            </div>
            <p className="text-[0.8rem] text-muted-foreground">
              Roles are managed through authorization settings
            </p>
          </div>
        </div>

        <Separator />

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" isLoading={form.formState.isSubmitting}>
            Save Changes
          </Button>
        </div>
      </form>
    </Form>
  );
}
